import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { cn } from '@/lib/core';
import type { ProductImageDto } from '@/services/platform/api/model';

interface ProductGalleryProps {
  images: ProductImageDto[];
  alt?: string;
  className?: string;
}

export function ProductGallery({ images, alt, className }: ProductGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const ordered = [...images].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  if (ordered.length === 0) {
    return <div className={cn('bg-jncquoi-neutral-50 aspect-3/5 w-full', className)} aria-hidden="true" />;
  }

  return (
    <div className={cn('relative w-full', className)}>
      <Swiper
        slidesPerView={1}
        onActiveIndexChange={swiper => setActiveIndex(swiper.activeIndex)}
        className="aspect-3/5 w-full"
      >
        {ordered.map((image, i) => (
          <SwiperSlide key={`${image.src}-${i}`}>
            <div className="bg-jncquoi-neutral-50 h-full w-full">
              {image.src && (
                <img
                  src={image.src}
                  alt={image.altText ?? alt ?? ''}
                  className="h-full w-full object-cover"
                  loading={i === 0 ? 'eager' : 'lazy'}
                />
              )}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-10"
        style={{
          height: 'calc(max(1rem, env(safe-area-inset-top)) + 5rem)',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 100%)',
        }}
        aria-hidden="true"
      />

      {ordered.length > 1 && (
        <div
          className="absolute right-0 bottom-4 left-0 z-10 flex items-center justify-center gap-2"
          aria-hidden="true"
        >
          {ordered.map((_, i) => (
            <span
              key={i}
              className={cn(
                'block h-px w-3.5 transition-colors duration-300',
                i === activeIndex ? 'bg-jncquoi-neutral-1000' : 'bg-jncquoi-neutral-1000/40'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
